// NoteModal.jsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import swal from "sweetalert";
import {
  saveNote,
  updateNote,
  deleteNote,
} from "../../../store/features/dealProgress/noteActions";

const NoteModal = ({
  stage,
  stageName,
  notes,
  userRole,
  onClose,
  businessDetails,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [noteType, setNoteType] = useState("information");
  const [noteText, setNoteText] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [characterCount, setCharacterCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const user = JSON.parse(sessionStorage.getItem("userDetails"))?.result;

  const maxCharacters = 500;

  // Check if user can modify notes
  const canModifyNotes = true;

  // Check if user can see/modify process notes
  const canAccessProcessNotes = true;

  // Check if notes exist for both types
  const hasInformationNote =
    notes.information && Object.keys(notes.information).length > 0;
  const hasProcessNote = notes.process && Object.keys(notes.process).length > 0;
  const hasAnyNotes = hasInformationNote || hasProcessNote;

  // permissions
  const canCreateNote =
    user?.role?.id === 1 ||
    user?.role?.id === 2 ||
    user?.role?.id === 4 ||
    user?.role?.id === 5 ||
    (user?.role?.id === 3 && user?.loggedinprofiletype === "sellerprofile");
  const canSeeProcessNote =
    hasProcessNote &&
    (user?.role?.id === 1 ||
      user?.role?.id === 2 ||
      user?.role?.id === 4 ||
      user?.role?.id === 5 ||
      (user?.role?.id === 3 && user?.loggedinprofiletype === "sellerprofile"));

  const canEditNote =
    user?.role?.id === 1 ||
    user?.role?.id === 2 ||
    user?.role?.id === 4 ||
    user?.role?.id === 5 ||
    (user?.role?.id === 3 && user?.loggedinprofiletype === "sellerprofile");

  const seeNoNoteMsg =
    !hasInformationNote &&
    user?.role?.id === 3 &&
    user?.loggedinprofiletype !== "sellerprofile";

  const canSeeInformationNote =
    user?.role?.id === 1 ||
    user?.role?.id === 2 ||
    user?.role?.id === 4 ||
    user?.role?.id === 5 ||
    (user?.role?.id === 3 && user?.loggedinprofiletype === "sellerprofile");

  useEffect(() => {
    // Auto-save draft to localStorage
    const draftKey = `note_draft_${stage.id}_${noteType}`;
    const interval = setInterval(() => {
      if (noteText.trim() && !isEditing) {
        localStorage.setItem(draftKey, noteText);
      }
    }, 10000); // Save every 10 seconds

    return () => clearInterval(interval);
  }, [noteText, stage.id, noteType, isEditing]);

  useEffect(() => {
    // Load draft on mount if not editing existing note
    if (!isEditing) {
      const draftKey = `note_draft_${stage.id}_${noteType}`;
      const draft = localStorage.getItem(draftKey);
      if (draft) {
        setNoteText(draft);
        setCharacterCount(draft.length);
      }
    }
  }, [stage.id, noteType, isEditing]);

  const handleTextChange = (e) => {
    const text = e.target.value;
    if (text.length <= maxCharacters) {
      setNoteText(text);
      setCharacterCount(text.length);
      setError("");
    }
  };

  const handleSave = async () => {
    if (!noteText.trim()) {
      setError(t("noteTextRequired"));
      return;
    }

    if (!canModifyNotes) {
      setError(t("noPermissionToSave"));
      return;
    }

    if (noteType === "process" && !canAccessProcessNotes) {
      setError(t("noPermissionToSaveProcess"));
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      if (isEditing && editingNote) {
        // Update existing note
        const updateData = {
          note: noteText.trim(),
          author: user?.user?.id,
          lang: i18n.language,
          note_type: noteType,
          business_id: businessDetails.id,
          progress_id: stage.id,
        };

        console.log("Updating note with data:", updateData);
        const result = await dispatch(updateNote(editingNote.id, updateData));
        console.log("Update note result:", result);
      } else {
        // Create new note
        const noteData = {
          progress_id: stage.id,
          note_type: noteType,
          author: user?.user?.id,
          business_id: businessDetails.id,
          note: noteText.trim(),
          lang: i18n.language,
        };

        console.log("Saving note with data:", noteData);
        const result = await dispatch(saveNote(noteData));
        console.log("Save note result:", result);
      }

      // Clear draft after successful save
      const draftKey = `note_draft_${stage.id}_${noteType}`;
      localStorage.removeItem(draftKey);

      // Reset form
      setNoteText("");
      setCharacterCount(0);
      setIsEditing(false);
      setEditingNote(null);

      // Close modal after successful save
      onClose();
    } catch (err) {
      console.error("Error saving/updating note:", err);
      setError(err.message || t("errorSavingNote"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (type) => {
    const existingNote = notes[type];
    if (existingNote) {
      setNoteType(type);
      setNoteText(existingNote.text);
      setCharacterCount(existingNote.text.length);
      setIsEditing(true);
      setEditingNote(existingNote);
    }
  };

  const handleDelete = async (type) => {
    const result = await swal({
      title: t("confirmDeleteNote"),
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!result) {
      return;
    }

    const noteToDelete = notes[type];
    if (!noteToDelete) return;

    setIsLoading(true);
    try {
      await dispatch(
        deleteNote({
          noteId: noteToDelete.id,
          progressId: stage.id,
          noteType: type,
        })
      );
      swal(t("noteDeleted"), "", "success");
    } catch (err) {
      setError(t("errorDeletingNote"));
      swal(t("errorDeletingNote"), "", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Clear any drafts
    const draftKey = `note_draft_${stage.id}_${noteType}`;
    localStorage.removeItem(draftKey);
    onClose();
  };

  const handleAddNewNote = () => {
    setIsEditing(true);
    setEditingNote(null);
    setNoteText("");
    setCharacterCount(0);
    // Set default note type based on what doesn't exist yet
    if (!hasInformationNote) {
      setNoteType("information");
    } else if (!hasProcessNote && canAccessProcessNotes) {
      setNoteType("process");
    } else {
      setNoteType("information");
    }
  };

  const getPlaceholderText = () => {
    const lang = i18n.language.toLowerCase();
    if (noteType === "process") {
      switch (lang) {
        case "fr":
          return (
            t("processPlaceholder") ||
            "ex: En attente de signature du contrat par l'acheteur"
          );
        case "es":
          return (
            t("processPlaceholder") ||
            "ej: Esperando que el comprador firme el contrato"
          );
        default:
          return (
            t("processPlaceholder") ||
            "e.g., Waiting for contract to be signed by the buyer"
          );
      }
    } else {
      switch (lang) {
        case "fr":
          return (
            t("infoPlaceholder") ||
            "ex: La banque vient d'approuver le prêt pour cette entreprise"
          );
        case "es":
          return (
            t("infoPlaceholder") ||
            "ej: El banco acaba de aprobar el préstamo para este negocio"
          );
        default:
          return (
            t("infoPlaceholder") ||
            "e.g., Bank just approved the loan for this business"
          );
      }
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  // Determine what to show
  const showExistingNotes = !isEditing && hasAnyNotes;
  const canAddMoreNotes =
    canModifyNotes &&
    (!hasInformationNote || (!hasProcessNote && canAccessProcessNotes));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="note-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {isEditing ? t("editNote") : t("addNoteTitle")}: {stageName}
          </h3>
          <button
            className="close-button"
            onClick={onClose}
            aria-label={t("close")}
          >
            ×
          </button>
        </div>

        <div className="modal-content">
          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}

          {showExistingNotes && (
            <div className="existing-notes">
              {notes.process && canAccessProcessNotes && canSeeProcessNote && (
                <div className="existing-note process">
                  <div className="note-header">
                    <span className="note-type-label">
                      {t("processNoteLabel")}
                    </span>
                    {canModifyNotes && canEditNote && (
                      <div className="note-actions">
                        <button
                          className="edit-button"
                          onClick={() => handleEdit("process")}
                          disabled={isLoading}
                        >
                          {t("editButton")}
                        </button>
                        <button
                          className="delete-button"
                          onClick={() => handleDelete("process")}
                          disabled={isLoading}
                        >
                          {t("deleteButton")}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="note-text">{notes.process.text}</div>
                  {/* <div className="note-meta">
                    {t("lastUpdatedLabel")}:{" "}
                    {formatTimestamp(
                      notes.process.lastModified || notes.process.timestamp
                    )}
                    {notes.process.author &&
                      ` ${t("by")} ${notes.process.author}`}
                  </div> */}
                </div>
              )}

              {notes.information && (
                <div className="existing-note information">
                  <div className="note-header">
                    <span className="note-type-label">
                      {t("infoNoteLabel")}
                    </span>
                    {canModifyNotes && canEditNote && (
                      <div className="note-actions">
                        <button
                          className="edit-button"
                          onClick={() => handleEdit("information")}
                          disabled={isLoading}
                        >
                          {t("editButton")}
                        </button>
                        <button
                          className="delete-button"
                          onClick={() => handleDelete("information")}
                          disabled={isLoading}
                        >
                          {t("deleteButton")}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="note-text">{notes.information.text}</div>
                  {/* <div className="note-meta">
                    {t("lastUpdatedLabel")}:{" "}
                    {formatTimestamp(
                      notes.information.lastModified ||
                        notes.information.timestamp
                    )}
                    {notes.information.author &&
                      ` ${t("by")} ${notes.information.author}`}
                  </div> */}
                </div>
              )}

              {canAddMoreNotes && canCreateNote && (
                <button
                  className="btn btn-primary"
                  onClick={handleAddNewNote}
                  disabled={isLoading}
                >
                  {t("addNewNote")}
                </button>
              )}
            </div>
          )}
          {seeNoNoteMsg && (
            <div className="existing-note information">
              <div className="note-text text-center">{t("noNoteMsg")}</div>
            </div>
          )}

          {(isEditing || !hasAnyNotes) && canModifyNotes && canCreateNote && (
            <div className="note-form">
              <div className="note-type-selection">
                <label className="note-type-label">{t("noteTypeLabel")}:</label>
                <div className="radio-group">
                  {/* Only show process option if user can access it and either editing existing process note or no process note exists */}
                  {canAccessProcessNotes &&
                    ((isEditing && editingNote?.noteType === "process") ||
                      (!isEditing && !hasProcessNote)) && (
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="noteType"
                          value="process"
                          checked={noteType === "process"}
                          onChange={(e) => setNoteType(e.target.value)}
                          disabled={isLoading}
                        />
                        <span className="radio-label">
                          {t("processNoteLabel")}
                          <small className="visibility-text">
                            ({t("processVisibilityText")})
                          </small>
                        </span>
                      </label>
                    )}

                  {/* Only show information option if either editing existing information note or no information note exists */}
                  {((isEditing && editingNote?.noteType === "information") ||
                    (!isEditing && !hasInformationNote)) && (
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="noteType"
                        value="information"
                        checked={noteType === "information"}
                        onChange={(e) => setNoteType(e.target.value)}
                        disabled={isLoading}
                      />
                      <span className="radio-label">
                        {t("infoNoteLabel")}
                        <small className="visibility-text">
                          ({t("infoVisibilityText")})
                        </small>
                      </span>
                    </label>
                  )}
                </div>
              </div>

              <div className="textarea-container">
                <textarea
                  value={noteText}
                  onChange={handleTextChange}
                  placeholder={getPlaceholderText()}
                  className="note-textarea"
                  rows={4}
                  disabled={isLoading}
                  aria-describedby="char-count"
                />
                <div id="char-count" className="character-count">
                  {t("charactersLabel")}: {characterCount}/{maxCharacters}
                </div>
              </div>
            </div>
          )}

          {!canModifyNotes && !hasAnyNotes && (
            <div className="no-permission-message">
              {t("noPermissionToAddNotes")}
            </div>
          )}

          {/* {!canAddMoreNotes && !isEditing && hasAnyNotes && canModifyNotes && (
            <div className="no-more-notes-message">
              <p>
                {t("allNoteTypesExist") ||
                  "All note types already exist for this stage. You can edit existing notes using the edit button above."}
              </p>
            </div>
          )} */}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-outline-secondary"
            onClick={handleCancel}
            disabled={isLoading}
          >
            {t("cancelButton")}
          </button>

          {canModifyNotes &&
            canCreateNote &&
            (isEditing || !hasAnyNotes || canAddMoreNotes) && (
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={isLoading || !noteText.trim()}
              >
                {isLoading
                  ? t("saving")
                  : isEditing
                  ? t("updateButton")
                  : t("saveButton")}
              </button>
            )}
        </div>
      </div>
    </div>
  );
};

export default NoteModal;
